import { useRef, useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { CloseButton } from '@/components/form/baseInputs/Button/CloseButton';
import { useMultiDocumentUploaderContext } from '@/modules/documents/MultiDocumentUploader/context/multiDocumentUploader.context';

import { useDocumentUploadStepContext } from '../steps/documentUpload/context/documentUploadStep.context';
import { usePersistUploadedDocuments } from '../steps/documentUpload/hooks/usePersistUploadedDocuments';

interface SmartReviewOnboardingHeaderActionProps {
  isFirstStep: boolean;
  onClose: () => void;
}

/**
 * Closes the first step immediately. On later steps, it saves all uploaded
 * documents before closing so the user can complete onboarding later.
 */
export function SmartReviewOnboardingHeaderAction({
  isFirstStep,
  onClose,
}: SmartReviewOnboardingHeaderActionProps) {
  const { hasBeenClosed, markCommitted } = useDocumentUploadStepContext();
  const { whenUploadsSettle } = useMultiDocumentUploaderContext();
  const { persist } = usePersistUploadedDocuments();
  const [isSaving, setIsSaving] = useState(false);

  // The uploaded-file set can grow while uploads settle, so use the latest
  // persist callback after the wait instead of the callback captured on click.
  const persistRef = useRef(persist);
  persistRef.current = persist;

  if (isFirstStep) {
    return <CloseButton size="xs" aria-label="Close" onClick={onClose} />;
  }

  const handleSaveAndClose = async () => {
    markCommitted();
    setIsSaving(true);
    try {
      await whenUploadsSettle();
      const documentIds = await persistRef.current();
      if (hasBeenClosed() || !documentIds) return;
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Button
      variant="secondary"
      size="sm"
      onClick={handleSaveAndClose}
      loading={isSaving}
    >
      Save and complete later
    </Button>
  );
}
